import { registerPlugin, WebPlugin } from '@capacitor/core';

/**
 * Ein `multipart/form-data`-Upload über den nativen Weg.
 *
 * Auf dem Gerät dahinter: `ios/App/App/MultipartUploadPlugin.swift`. Gebraucht wird es, weil
 * der Classic-Server keine CORS-Kopfzeilen schickt — aus dem WKWebView heraus kommt `fetch`
 * gar nicht erst hinaus — und weil `CapacitorHttp` keine Binärdaten in einem multipart-Rumpf
 * trägt.
 *
 * Das Plugin kennt weder Ablage noch Feldnamen: es bekommt Bytes und ein Wörterbuch. Wer was
 * hochlädt, entscheidet `src/server/classic/hochladen.ts`.
 */

export interface MultipartUploadOptions {
  url: string;
  /** Die Formularfelder neben der Datei. */
  fields: Record<string, string>;
  /** Zusätzliche Kopfzeilen — hier die Anmeldung. */
  headers: Record<string, string>;
  fileName: string;
  mimeType: string;
  /** Die Datei als Base64, ohne `data:`-Vorspann. */
  base64: string;
  /** Name des Dateiteils, Vorgabe `file`. */
  fileField?: string;
  timeoutSeconds?: number;
}

export interface MultipartUploadResult {
  status: number;
  ok: boolean;
  text: string;
}

export interface RohUploadOptions {
  url: string;
  headers: Record<string, string>;
  contentType: string;
  base64: string;
  timeoutSeconds?: number;
}

export interface MultipartUploadPlugin {
  upload(options: MultipartUploadOptions): Promise<MultipartUploadResult>;
  /** Ein roher `POST` mit den Bytes als Rumpf (Classics Diagnose-ZIP). */
  uploadRoh(options: RohUploadOptions): Promise<MultipartUploadResult>;
}

/**
 * Im Browser gibt es diesen Weg nicht — und er wird dort auch nicht gebraucht: dort trägt
 * `fetch` mit `FormData` (s. `server/classic/hochladen.ts::fetchMultipartSender`).
 *
 * Bewusst ein **Fehler** und keine leere Antwort: eine stillschweigend verworfene Übertragung
 * sähe in der Zählung wie ein Erfolg aus, und die Datei fehlte auf dem Server, ohne dass es
 * jemand merkte.
 */
class MultipartUploadWeb extends WebPlugin implements MultipartUploadPlugin {
  async upload(): Promise<MultipartUploadResult> {
    throw new Error('Der native Upload steht nur auf dem Gerät zur Verfügung.');
  }

  async uploadRoh(): Promise<MultipartUploadResult> {
    throw new Error('Der native Upload steht nur auf dem Gerät zur Verfügung.');
  }
}

export const MultipartUpload = registerPlugin<MultipartUploadPlugin>('MultipartUpload', {
  web: () => new MultipartUploadWeb(),
});
